import React from 'react';
import { ChevronDown, ChevronUp, Languages, Check, Headphones, User, AlertTriangle } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors" data-testid="btn-toggle-interpretacion">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">{title}</span>
          {badge && <span className="px-2 py-0.5 bg-[#aa2aff] text-white text-xs rounded-full">{badge}</span>}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && <div className="p-4 bg-[#080c2d]/50">{children}</div>}
    </div>
  );
}

export function InterpretacionSection({ isOpen, onToggle, data = {}, onChange, formatPrice, currency, settings = {} }) {
  const pricePerHour = settings.price_interprete || 1500;
  const priceCabina = settings.price_cabina_audio || 7500;
  const priceEquipoTx = settings.price_equipo_tx || 450;
  const priceTecnico = settings.price_tecnico_interp || 1500;
  const minHours = 4;

  const horas = data.horas || 0;
  const dias = data.dias || 0;
  const effectiveHoras = horas > 0 && horas < minHours ? minHours : horas;

  // Intérprete
  const interpreteSubtotal = horas > 0 ? pricePerHour * effectiveHoras * (dias || 1) : 0;

  // Cabina
  const cabinaDias = data.cabina ? (data.cabinaDias || dias || 1) : 0;
  const cabinaSubtotal = data.cabina ? priceCabina * cabinaDias : 0;

  // Equipo TX/RX portátil
  const equipoQty = data.equipoTxQty || 0;
  const equipoDias = data.equipoTxDias || 0;
  const equipoSubtotal = priceEquipoTx * equipoQty * equipoDias;

  // Técnico de audio
  const tecnicoDias = data.tecnicoDias || 0;
  const tecnicoSubtotal = priceTecnico * tecnicoDias;

  const subtotal = interpreteSubtotal + cabinaSubtotal + equipoSubtotal + tecnicoSubtotal;

  const handleChange = (field, value) => onChange({ ...data, [field]: value });
  const badge = subtotal > 0 ? formatPrice(subtotal) : null;

  return (
    <CollapsibleSection title="Interpretación Simultánea" icon={Languages} isOpen={isOpen} onToggle={onToggle} badge={badge}>
      <div className="space-y-4">
        {/* Intérprete */}
        <div className="bg-[#aa2aff]/10 rounded-lg p-3 border border-[#aa2aff]/20">
          <p className="text-white text-sm font-medium">Intérprete Bilingüe (Inglés - Español / Español - Inglés)</p>
          <p className="text-slate-400 text-xs mt-1">{formatPrice(pricePerHour)} {currency} / hora · Llamado mínimo {minHours} horas</p>
          <p className="text-slate-500 text-xs mt-0.5 italic">No incluye cabina ni equipo de audio.</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Horas por día</Label>
            <Input type="number" min="0" max="24" value={data.horas || ''} onChange={(e) => handleChange('horas', parseInt(e.target.value) || 0)}
              placeholder={`Mín. ${minHours}`} className="bg-[#080c2d]/50 border-[#272b96] text-white focus:border-[#aa2aff] h-10" data-testid="interpretacion-horas" />
            {horas > 0 && horas < minHours && (
              <p className="text-amber-400 text-xs">Llamado mínimo de {minHours} horas. Se cobrará como {minHours} hrs.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Días</Label>
            <Input type="number" min="0" max="365" value={data.dias || ''} onChange={(e) => handleChange('dias', parseInt(e.target.value) || 0)}
              placeholder="1" className="bg-[#080c2d]/50 border-[#272b96] text-white focus:border-[#aa2aff] h-10" data-testid="interpretacion-dias" />
          </div>
        </div>

        {/* Cabina de Audio */}
        <button onClick={() => handleChange('cabina', !data.cabina)}
          className={cn("w-full p-4 rounded-xl border text-left transition-all flex items-center justify-between",
            data.cabina ? "border-[#aa2aff] bg-[#aa2aff]/15" : "border-white/10 bg-white/5 hover:border-[#aa2aff]/40"
          )} data-testid="btn-cabina-audio">
          <div className="flex items-center gap-3">
            <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0",
              data.cabina ? "border-[#aa2aff] bg-[#aa2aff]" : "border-slate-500")}>
              {data.cabina && <Check className="w-3 h-3 text-white" />}
            </div>
            <div>
              <span className={cn("text-sm font-medium", data.cabina ? "text-[#aa2aff]" : "text-white")}>Agregar cabina de audio</span>
              <p className="text-slate-400 text-xs mt-0.5">{formatPrice(priceCabina)} {currency} / día</p>
            </div>
          </div>
          {data.cabina && (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Label className="text-slate-400 text-xs">Días:</Label>
              <Input type="number" min="1" max="30" value={data.cabinaDias || dias || ''}
                onChange={(e) => { e.stopPropagation(); handleChange('cabinaDias', parseInt(e.target.value) || 0); }}
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm" data-testid="cabina-dias" />
            </div>
          )}
        </button>

        {/* Equipo TX/RX Portátil */}
        <div className={cn("p-4 rounded-xl border transition-all",
          equipoSubtotal > 0 ? "border-[#aa2aff] bg-[#aa2aff]/10" : "border-white/10 bg-white/5")}>
          <div className="flex items-start gap-3 mb-3">
            <Headphones className="w-5 h-5 text-[#aa2aff] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-white text-sm font-medium">Equipo de Traducción Simultánea Portátil</h4>
              <p className="text-slate-400 text-xs mt-0.5">Receptores con audífonos multicanal inalámbricos y 1 TX Transmisor FM Kit para intérpretes. Equipo para 2 idiomas.</p>
              <p className="text-slate-400 text-xs mt-0.5">{formatPrice(priceEquipoTx)} {currency} / unidad / día</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 ml-8">
            <div className="space-y-1">
              <Label className="text-slate-500 text-xs">Cantidad de equipos</Label>
              <Input type="number" min="0" max="200" value={data.equipoTxQty || ''} onChange={(e) => handleChange('equipoTxQty', parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white h-8 text-sm" data-testid="equipo-tx-qty" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-500 text-xs">Días</Label>
              <Input type="number" min="0" max="30" value={data.equipoTxDias || ''} onChange={(e) => handleChange('equipoTxDias', parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white h-8 text-sm" data-testid="equipo-tx-dias" />
            </div>
          </div>
          {equipoSubtotal > 0 && <p className="text-[#aa2aff] text-xs font-semibold mt-2 ml-8">{equipoQty} equipos × {equipoDias} días × {formatPrice(priceEquipoTx)} = {formatPrice(equipoSubtotal)}</p>}
        </div>

        {/* Técnico de Audio */}
        <div className={cn("p-4 rounded-xl border transition-all",
          tecnicoSubtotal > 0 ? "border-[#aa2aff] bg-[#aa2aff]/10" : "border-white/10 bg-white/5")}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-[#aa2aff] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white text-sm font-medium">Técnico de Audio para Interpretación Simultánea</h4>
                <p className="text-slate-400 text-xs mt-0.5">Requerido en la contratación de cabina o equipos TX/RX</p>
                <p className="text-slate-400 text-xs mt-0.5">{formatPrice(priceTecnico)} {currency} / día</p>
              </div>
            </div>
            <div className="text-center flex-shrink-0">
              <Label className="text-slate-500 text-xs">Días</Label>
              <Input type="number" min="0" max="30" value={data.tecnicoDias || ''} onChange={(e) => handleChange('tecnicoDias', parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm" data-testid="tecnico-interp-dias" />
            </div>
          </div>
          {tecnicoSubtotal > 0 && <p className="text-[#aa2aff] text-xs font-semibold mt-2 ml-8">= {formatPrice(tecnicoSubtotal)}</p>}
        </div>

        {/* Nota de pérdida/daño */}
        <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-slate-400 text-xs leading-relaxed">
            <span className="text-amber-400 font-medium">Nota:</span> En caso de pérdida o daño al receptor se cobrarán $220 USD por receptor y $50 USD por audífono.
          </p>
        </div>

        {/* Subtotal */}
        {subtotal > 0 && (
          <div className="pt-4 border-t border-white/10">
            <div className="bg-[#1a1f50]/50 rounded-lg p-3 space-y-1">
              {interpreteSubtotal > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Intérprete: {formatPrice(pricePerHour)} × {effectiveHoras} hrs × {dias || 1} día{(dias || 1) > 1 ? 's' : ''}</span>
                  <span className="text-white font-medium">{formatPrice(interpreteSubtotal)}</span>
                </div>
              )}
              {cabinaSubtotal > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Cabina de audio: {cabinaDias} día{cabinaDias > 1 ? 's' : ''}</span>
                  <span className="text-white font-medium">{formatPrice(cabinaSubtotal)}</span>
                </div>
              )}
              {equipoSubtotal > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Equipo TX/RX: {equipoQty} × {equipoDias} días</span>
                  <span className="text-white font-medium">{formatPrice(equipoSubtotal)}</span>
                </div>
              )}
              {tecnicoSubtotal > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Técnico de audio: {tecnicoDias} día{tecnicoDias > 1 ? 's' : ''}</span>
                  <span className="text-white font-medium">{formatPrice(tecnicoSubtotal)}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-lg pt-1 border-t border-white/10">
                <span className="text-white font-semibold">Subtotal Interpretación:</span>
                <span className="text-[#aa2aff] font-bold">{formatPrice(subtotal)} {currency}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export function calcInterpretacionTotal(data = {}, settings = {}) {
  const price = settings.price_interprete || 1500;
  const priceCabina = settings.price_cabina_audio || 7500;
  const priceEquipoTx = settings.price_equipo_tx || 450;
  const priceTecnico = settings.price_tecnico_interp || 1500;

  const horas = data.horas || 0;
  const dias = data.dias || 0;
  const effectiveHoras = horas > 0 && horas < 4 ? 4 : horas;
  const interpreteTotal = horas > 0 ? price * effectiveHoras * (dias || 1) : 0;

  const cabinaDias = data.cabina ? (data.cabinaDias || dias || 1) : 0;
  const cabinaTotal = data.cabina ? priceCabina * cabinaDias : 0;

  const equipoTotal = priceEquipoTx * (data.equipoTxQty || 0) * (data.equipoTxDias || 0);
  const tecnicoTotal = priceTecnico * (data.tecnicoDias || 0);

  return interpreteTotal + cabinaTotal + equipoTotal + tecnicoTotal;
}
